import { FC, useEffect, useState } from 'react';
import { FullPageLoader, GenericTable, Loader } from '@components';
import { Box, Typography } from '@mui/material';
import DownloadReportsPdf from '../components/DownloadReports/DownloadReportsPdf';
import DownloadReportsHeader from '../components/DownloadReports/DownloadReportsHeader';
import { Game } from '../components/FundingEligibility/types';
import useCharts from '@hooks/api/useCharts';
import useChartById from '@hooks/api/useChartById';
import DownloadChart from '../components/DownloadChart';
import { processRowDataWithMetadata } from '../../../components/GenericTable/tableDataUtil';
import {
  Column,
  Row,
} from '../components/FinancialProjections/FinancialProjectionsTable/FinancialProjectionsTable';

interface ChartListData {
  id: string;
  chartType: string;
  name: string;
  metadata?: string;
}

interface ChartData {
  id: string;
  name: string;
  columns: Column<Record<string, any>>[];
  rows: Row<Record<string, any>>[];
  metadata: {
    [key: string]: any;
  };
}

const DownloadReports: FC<{ id: string }> = ({ id }) => {
  const [dashboardLoader, setDashboardLoader] = useState<boolean>(true);
  const [componentLoader, setComponentLoader] = useState<boolean>(true);
  const [dashboardData, setDashboardData] = useState<ChartListData[]>([]);
  const [chartResponse, setChartResponse] = useState<ChartData[]>([]);
  const [selectedGame, setSelectedGame] = useState<Game | undefined>();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState<boolean>(false);

  const { getChartData } = useCharts();
  const { getChartDataById } = useChartById();

  const fetchDashboardData = async () => {
    setDashboardLoader(true);
    try {
      const response = await getChartData(id);
      setDashboardData(response);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchChartResponse = async (chartId: string) => {
    try {
      if (dashboardData) {
        const response = await getChartDataById(id, chartId, { gameId: selectedGame?.id || '' });
        if (response) {
          const { rows, columns, metadata } = formatChartResponseData(response);
          setChartResponse((prev) => {
            const exists = prev.find((item) => item.id === chartId);

            if (exists) {
              return prev.map((item) =>
                item.id === chartId
                  ? { id: chartId, name: response.name, metadata, rows, columns }
                  : item,
              );
            } else {
              return [...prev, { id: chartId, name: response.name, metadata, rows, columns }];
            }
          });
        }
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [id]);

  useEffect(() => {
    if (selectedGame && dashboardData?.length > 0) {
      setComponentLoader(true);
      const principalChart = dashboardData.find(
        (chart) => chart.name.trim() === 'Principal & Interest Payment',
      );
      const balanceChart = dashboardData.find(
        (chart) => chart.name.trim() === 'Outstanding Balance',
      );

      const fetchAllCharts = async () => {
        try {
          const promises = [];
          if (principalChart) {
            promises.push(fetchChartResponse(principalChart.id));
          }
          if (balanceChart) {
            promises.push(fetchChartResponse(balanceChart.id));
          }
          await Promise.all(promises);
        } catch (error) {
          console.error('Error fetching charts:', error);
        } finally {
          setComponentLoader(false);
        }
      };

      fetchAllCharts();
    }
  }, [selectedGame, dashboardData]);

  const formatChartResponseData = (response: any) => {
    let columns = [];
    let rows = [];
    const metadata = JSON.parse(response.metadata);
    const { mappedColumns, mappedData } = processRowDataWithMetadata(
      response.name,
      metadata,
      response.result.data,
    );
    columns = mappedColumns;
    rows = mappedData;

    return { rows, columns, metadata };
  };

  const getPrincipalChartData = () => {
    return chartResponse.find((chart) => chart.name?.trim() === 'Principal & Interest Payment');
  };

  const getBalanceChartData = () => {
    return chartResponse.find((chart) => chart.name?.trim() === 'Outstanding Balance');
  };

  return (
    <>
      {dashboardLoader && <FullPageLoader hasSideNav={true} />}
      <div
        style={{
          width: '100%',
          height: '100vh',
          border: 'none',
          overflow: 'auto',
          padding: '1rem 2rem 5rem',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        <DownloadReportsHeader
          isDownloadModalOpen={isDownloadModalOpen}
          setIsDownloadModalOpen={setIsDownloadModalOpen}
          submoduleId={id}
          selectedGame={selectedGame}
          setSelectedGame={setSelectedGame}
          setDashboardLoader={setDashboardLoader}
        />
        <Box
          sx={{
            width: '100%',
            padding: '0.875rem',
            backgroundColor: '#FFF',
            borderRadius: '0.75rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '0.25rem',
              justifyContent: 'space-between',
              marginBottom: '1rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 700,
                color: '#162936',
                lineHeight: '1.375rem',
              }}
            >
              Principal & Interest Payment
            </Typography>

            <DownloadChart
              fileName={getPrincipalChartData()?.name || 'Principal & Interest Payment'}
              submoduleId={id}
              chartId={getPrincipalChartData()?.id || ''}
              filterSelection={{ gameId: selectedGame?.id || '' }}
            />
          </Box>
          {componentLoader ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '8.75rem',
              }}
            >
              <Loader />
            </Box>
          ) : (
            <GenericTable
              data={getPrincipalChartData()?.rows || []}
              columns={getPrincipalChartData()?.columns || []}
              meta={getPrincipalChartData()?.metadata || []}
            />
          )}
        </Box>
        <Box
          sx={{
            width: '100%',
            padding: '0.875rem',
            backgroundColor: '#FFF',
            borderRadius: '0.75rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '0.25rem',
              justifyContent: 'space-between',
              marginBottom: '1rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 700,
                color: '#162936',
                lineHeight: '1.375rem',
              }}
            >
              Outstanding Balance
            </Typography>
            <DownloadChart
              fileName={getBalanceChartData()?.name || 'Outstanding Balance'}
              submoduleId={id}
              chartId={getBalanceChartData()?.id || ''}
              filterSelection={{ gameId: selectedGame?.id || '' }}
            />
          </Box>
          {componentLoader ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '8.75rem',
              }}
            >
              <Loader />
            </Box>
          ) : (
            <GenericTable
              data={getBalanceChartData()?.rows || []}
              columns={getBalanceChartData()?.columns || []}
              meta={getBalanceChartData()?.metadata || []}
            />
          )}
        </Box>
        {isDownloadModalOpen ? (
          <DownloadReportsPdf
            isOpen={isDownloadModalOpen}
            handleClose={() => setIsDownloadModalOpen(false)}
            selectedGame={selectedGame}
            getBalanceChartData={getBalanceChartData}
            getPrincipalChartData={getPrincipalChartData}
          />
        ) : (
          <></>
        )}
      </div>
    </>
  );
};

export default DownloadReports;
